import { useCallback, useEffect, useRef, useState } from "react"

import { describeMicrophoneError, requestMicrophone } from "@/lib/microphone"

// Module-level registry so only one useAudioRecorder instance is active at a time.
// When start() is called on instance B, instance A's stop() is invoked first.
let activeRecorder = null

// Module-level in-flight promise. Guards against the double-start race where
// two mic buttons tapped in rapid succession both pass the `activeRecorder`
// null check before either `getUserMedia` resolves.
let startingPromise = null

// Cap client-side recording at 60 seconds by default. Prevents accidental
// 10-minute clips from blowing the 10 MB upload cap (and the OpenRouter
// budget). Callers whose server endpoint allows longer clips override this via
// `maxRecordingMs` — the server cap is the one that actually binds, this just
// stops the browser wasting a recording the server would reject.
const MAX_RECORDING_MS = 60_000

// Default transcription endpoint. Callers with their own authorization scope
// (and their own duration cap) pass `endpoint`.
const DEFAULT_ENDPOINT = "/app/member/voice_transcriptions"

// Client-side upload deadline. If the server accepts the TCP connection and
// never responds, the AbortController fires and the UI transitions to error
// state instead of staying "uploading" forever.
const UPLOAD_DEADLINE_MS = 90_000

// Preferred MIME types in order. Chrome/Firefox pick webm/opus; Safari picks mp4.
const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg",
]

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return null
  for (const type of PREFERRED_MIME_TYPES) {
    try {
      if (MediaRecorder.isTypeSupported(type)) return type
    } catch {
      // Some browsers throw on isTypeSupported for unknown types
    }
  }
  return "" // empty = browser default
}

function readCsrfToken() {
  if (typeof document === "undefined") return null
  return document.querySelector('meta[name="csrf-token"]')?.content || null
}

export function useAudioRecorder({
  onTranscript,
  onError,
  endpoint = DEFAULT_ENDPOINT,
  maxRecordingMs = MAX_RECORDING_MS,
} = {}) {
  const [isSupported] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.MediaRecorder !== "undefined" &&
      typeof navigator !== "undefined" &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function"
  )
  const [state, setState] = useState("idle") // idle | requesting | recording | uploading | error
  const [error, setError] = useState(null)

  const recorderRef = useRef(null)
  const streamRef = useRef(null)
  const chunksRef = useRef([])
  const mimeTypeRef = useRef("")
  const autoStopTimerRef = useRef(null)
  const abortRef = useRef(null)
  const isMountedRef = useRef(true)
  // This instance's identity for the module-level activeRecorder registry.
  // Stored in a ref (not mutated onto the cleanup function object) so cleanup's
  // identity check survives dependency-array changes.
  const instanceIdRef = useRef(null)

  const setSafeState = useCallback((next) => {
    if (isMountedRef.current) setState(next)
  }, [])

  const setSafeError = useCallback(
    (next) => {
      if (!isMountedRef.current) return
      setError(next)
      if (next) onError?.(next)
    },
    [onError]
  )

  const cleanup = useCallback(() => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current)
      autoStopTimerRef.current = null
    }
    if (recorderRef.current) {
      try {
        if (recorderRef.current.state !== "inactive") recorderRef.current.stop()
      } catch {}
      recorderRef.current = null
    }
    if (streamRef.current) {
      // Null the ref BEFORE calling getTracks so a throw can't leave a dangling
      // reference that keeps the mic LED on.
      const stream = streamRef.current
      streamRef.current = null
      try {
        stream.getTracks().forEach((t) => t.stop())
      } catch {}
    }
    chunksRef.current = []
    if (activeRecorder && activeRecorder._instanceId === instanceIdRef.current) {
      activeRecorder = null
    }
  }, [])

  const uploadBlob = useCallback(
    async (blob) => {
      setSafeState("uploading")
      const abort = new AbortController()
      abortRef.current = abort

      // Wall-clock deadline. If the server accepts TCP and never responds
      // (Puma thread exhaustion, network middlebox), the fetch won't hang
      // forever in "uploading" state.
      const deadline = setTimeout(() => abort.abort(), UPLOAD_DEADLINE_MS)

      try {
        const form = new FormData()
        const ext = (blob.type.match(/audio\/([^;]+)/) || [])[1] || "webm"
        form.append("audio", blob, `recording.${ext.split("/").pop()}`)

        const res = await fetch(endpoint, {
          method: "POST",
          headers: {
            "X-CSRF-Token": readCsrfToken() || "",
            Accept: "application/json",
          },
          credentials: "same-origin",
          // Don't silently follow a 302 to the HTML login page — surface it
          // as an auth error so the user knows to re-sign-in instead of
          // seeing a generic "No transcript was returned" message.
          redirect: "manual",
          body: form,
          signal: abort.signal,
        })

        // A manual-redirect response has type "opaqueredirect" and no body.
        if (res.type === "opaqueredirect" || res.status === 0) {
          setSafeError("Your session has expired. Refresh the page and sign in again.")
          setSafeState("error")
          return
        }

        let data = {}
        const contentType = res.headers.get("content-type") || ""
        if (contentType.includes("application/json")) {
          try {
            data = await res.json()
          } catch {
            // Malformed JSON body — fall through to status-based messaging
          }
        }

        if (!res.ok) {
          const msg =
            data.error ||
            (res.status === 429
              ? "You're going too fast. Please wait a moment and try again."
              : "Couldn't transcribe that recording. Please try again.")
          setSafeError(msg)
          setSafeState("error")
          return
        }

        if (data.transcript) {
          onTranscript?.(data.transcript)
        } else {
          setSafeError("No transcript was returned. Please try again.")
          setSafeState("error")
          return
        }

        setSafeState("idle")
      } catch (err) {
        if (err.name === "AbortError") {
          // User cancelled OR deadline fired. Map deadline to an error state,
          // explicit cancels just return to idle.
          if (!isMountedRef.current) return
          setSafeError("Upload timed out. Please try again.")
          setSafeState("error")
          return
        }
        setSafeError("Couldn't reach the server. Check your connection and try again.")
        setSafeState("error")
      } finally {
        clearTimeout(deadline)
        abortRef.current = null
      }
    },
    [endpoint, onTranscript, setSafeError, setSafeState]
  )

  const stop = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder) return
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current)
      autoStopTimerRef.current = null
    }
    try {
      if (recorder.state !== "inactive") recorder.stop()
    } catch {
      // Already stopped
    }
  }, [])

  const start = useCallback(async () => {
    if (!isSupported) return

    // Guard against the double-start race: two mic taps in quick succession
    // both observe activeRecorder === null and both call getUserMedia, leaving
    // one stream orphaned. Collapse concurrent starts to a single flight.
    if (startingPromise) return
    startingPromise = (async () => {
      // If any instance (including a prior mount of this one) is active,
      // stop it first. stop() is idempotent on inactive recorders.
      if (activeRecorder) {
        try {
          activeRecorder.stop()
        } catch {}
        activeRecorder = null
      }

      setSafeError(null)
      setSafeState("requesting")

      let stream
      try {
        stream = await requestMicrophone()
      } catch (err) {
        setSafeError(describeMicrophoneError(err))
        setSafeState("error")
        return
      }

      if (!isMountedRef.current) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }

      const mime = pickMimeType()
      mimeTypeRef.current = mime
      chunksRef.current = []

      let recorder
      try {
        recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream)
      } catch (err) {
        stream.getTracks().forEach((t) => t.stop())
        setSafeError(describeMicrophoneError(err, "Couldn't start the recorder."))
        setSafeState("error")
        return
      }

      streamRef.current = stream
      recorderRef.current = recorder

      // Mark this instance as the globally active recorder.
      const instanceId = Symbol("audio-recorder")
      instanceIdRef.current = instanceId
      activeRecorder = { _instanceId: instanceId, stop }

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onerror = (e) => {
        setSafeError(describeMicrophoneError(e.error, "Recording failed."))
        setSafeState("error")
        cleanup()
      }

      recorder.onstop = () => {
        if (autoStopTimerRef.current) {
          clearTimeout(autoStopTimerRef.current)
          autoStopTimerRef.current = null
        }
        // Release the mic immediately — recorder.stop() doesn't stop tracks.
        // Null the ref BEFORE calling getTracks so a throw can't leave a
        // dangling reference that keeps the mic LED on.
        if (streamRef.current) {
          const stream = streamRef.current
          streamRef.current = null
          try {
            stream.getTracks().forEach((t) => t.stop())
          } catch {}
        }

        const blob = new Blob(chunksRef.current, {
          type: mimeTypeRef.current || "audio/webm",
        })
        chunksRef.current = []
        recorderRef.current = null
        if (activeRecorder && activeRecorder._instanceId === instanceId) activeRecorder = null

        // Guard against unmount first so short-recording error paths don't
        // fire onError on a dead component.
        if (!isMountedRef.current) return

        // Guard against zero-byte recordings (user tapped stop immediately)
        if (blob.size < 512) {
          setSafeError("That recording was too short. Please try again.")
          setSafeState("error")
          return
        }

        uploadBlob(blob)
      }

      recorder.start()
      setSafeState("recording")

      // Hard cap: auto-stop after maxRecordingMs
      autoStopTimerRef.current = setTimeout(() => {
        try {
          if (recorder.state === "recording") recorder.stop()
        } catch {}
      }, maxRecordingMs)
    })().finally(() => {
      startingPromise = null
    })
  }, [cleanup, isSupported, maxRecordingMs, setSafeError, setSafeState, stop, uploadBlob])

  const reset = useCallback(() => {
    abortRef.current?.abort()
    cleanup()
    setSafeError(null)
    setSafeState("idle")
  }, [cleanup, setSafeError, setSafeState])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      abortRef.current?.abort()
      cleanup()
    }
  }, [cleanup])

  // What one tap of a mic button should do, given where we are. Lives here
  // because three components each wrote their own version and they drifted —
  // two of them left the button permanently dead after a blocked microphone,
  // with no way back but a page reload.
  const toggleRecording = useCallback(() => {
    if (state === "error") {
      // Clearing and immediately re-asking is also what recovers the button
      // once the member grants permission from the address bar.
      reset()
      start()
      return
    }
    if (state === "recording") {
      stop()
      return
    }
    if (state === "idle") start()
  }, [reset, start, state, stop])

  return { isSupported, state, error, start, stop, reset, toggleRecording }
}
